import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, Loader2, ArrowLeft, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { fetchInventoryOptimized, clearInventoryCache } from "@/lib/api/inventory";
import { saveDocument, generateDocumentNumber } from "@/lib/api/documents";
import { ProductSelector } from "@/components/ProductSelector";
import { CustomerSelector } from "@/components/CustomerSelector";
import { ColorBadge } from "@/components/ColorBadge";
import { Table, TableHeader, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ENTRY_MODE_OPTIONS = [
  { value: "normal", label: "Entrada normal" },
  { value: "adjustment", label: "Ajuste manual" },
  { value: "count", label: "Recuento" }
];

export function DocumentForm({ type, documentId = null, onClose }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [document, setDocument] = useState({
    number: "",
    date: new Date().toISOString().split('T')[0],
    customerId: "",
    items: [],
    entry_mode: "normal",
    regularization_reason: ""
  });
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantitiesBySize, setQuantitiesBySize] = useState({});
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [allSizes, setAllSizes] = useState([]);
  const { toast } = useToast();
  const entryMode = type === 'entrada' ? (document.entry_mode || 'normal') : null;
  const isAdjustmentMode = type === 'entrada' && entryMode === 'adjustment';
  const isCountMode = type === 'entrada' && entryMode === 'count';
  const isSpecialEntryMode = isAdjustmentMode || isCountMode;

  // Calcular totales del documento
  const documentTotals = document.items.reduce((acc, item) => {
    acc.totalQuantity += item.quantity;
    acc.totalAmount += item.quantity * item.price;
    return acc;
  }, { totalQuantity: 0, totalAmount: 0 });

  useEffect(() => {
    const checkRole = async () => {
      setIsLoadingRole(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: userData, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (error) throw error;
        const isAdminUser = userData.role === 'admin';
        setIsAdmin(isAdminUser);
        
        if (type === 'salida' && !isAdminUser) {
          toast({
            title: "Acceso denegado",
            description: "Solo los administradores pueden crear documentos de salida",
            variant: "destructive"
          });
          onClose();
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        toast({
          title: "Error",
          description: "No se pudo verificar el rol del usuario",
          variant: "destructive"
        });
      } finally {
        setIsLoadingRole(false);
      }
    };

    checkRole();
  }, [type]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (documentId) {
      fetchDocument();
    } else {
      generateNewDocumentNumber();
    }
  }, [documentId, type]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [productsRes, customersRes] = await Promise.all([
        supabase.from('products').select('*').eq('active', true),
        supabase.from('customers').select('*')
      ]);
      const inventoryData = await fetchInventoryOptimized(true);

      if (productsRes.error) throw productsRes.error;
      if (customersRes.error) throw customersRes.error;

      const productsData = productsRes.data || [];
      setProducts(productsData);
      setCustomers(customersRes.data || []);
      setInventory(inventoryData || []);

      // Definir el orden específico de las tallas
      const sizeOrder = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
      
      // Extraer todas las tallas únicas de los productos y ordenarlas según el orden específico
      const uniqueSizes = [...new Set(productsData.flatMap(p => p.sizes))]
        .sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b));
      setAllSizes(uniqueSizes);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos iniciales",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateNewDocumentNumber = async () => {
    try {
      const newNumber = await generateDocumentNumber(type);
      setDocument(prev => ({ ...prev, number: newNumber }));
    } catch (error) {
      console.error('Error generating document number:', error);
      toast({
        title: "Error",
        description: "No se pudo generar el número de documento",
        variant: "destructive"
      });
    }
  };

  const fetchDocument = async () => {
    setIsLoadingDocument(true);
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          *,
          items:document_items(*, product:products(*))
        `)
        .eq('id', documentId)
        .single();

      if (error) throw error;
      if (data) {
        const formattedDate = data.date ? new Date(data.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        
        setDocument({
          id: data.id,
          updated_at: data.updated_at,
          number: data.number,
          date: formattedDate,
          customerId: data.customer_id || "",
          entry_mode: data.entry_mode || 'normal',
          regularization_reason: data.regularization_reason || "",
          items: data.items?.map(item => ({
            ...item,
            theoretical_quantity: Number.isFinite(Number(item.theoretical_quantity)) ? Number(item.theoretical_quantity) : null,
            counted_quantity: Number.isFinite(Number(item.counted_quantity)) ? Number(item.counted_quantity) : null,
            product_code: item.product?.code,
            description: item.product?.description
          })) || []
        });
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el documento",
        variant: "destructive"
      });
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const calculateCurrentStock = (productId, size, color) => {
    return inventory
      .filter(item => 
        item.product_id === productId && 
        item.size === size && 
        item.color === color
      )
      .reduce((total, item) => total + item.quantity, 0);
  };

  const handleAddItem = async () => {
    const product = products.find(p => p.id === selectedProduct);
    if (!product || !selectedColor) return;

    const colorQuantities = quantitiesBySize[selectedColor] || {};
    const hasQuantities = isCountMode
      ? Object.values(colorQuantities).some(qty => qty !== "" && qty !== null && qty !== undefined)
      : isAdjustmentMode
        ? Object.values(colorQuantities).some(qty => (parseInt(qty) || 0) !== 0)
        : Object.values(colorQuantities).some(qty => (parseInt(qty) || 0) > 0);

    if (!hasQuantities) return;

    const newItems = product.sizes.map(size => {
      const currentStock = calculateCurrentStock(selectedProduct, size, selectedColor);
      const raw = colorQuantities[size];
      if (isCountMode) {
        if (raw === '' || raw === null || raw === undefined) return null;

        const countedQuantity = parseInt(raw);
        if (!Number.isFinite(countedQuantity) || countedQuantity < 0) return null;

        const quantity = countedQuantity - currentStock;
        if (quantity === 0) return null;

        return {
          product_id: selectedProduct,
          product_code: product.code,
          description: product.description,
          size,
          color: selectedColor,
          quantity,
          price: product.price,
          theoretical_quantity: currentStock,
          counted_quantity: countedQuantity
        };
      }

      const quantity = (typeof raw === 'string' && (raw === '' || raw === '-')) ? 0 : (parseInt(raw) || 0);
      // Reglas: en salida solo positivos; en entrada normal solo positivos; en ajuste manual permitir negativos y positivos, pero no 0
      if (type === 'salida' && quantity <= 0) return null;
      if (type === 'entrada' && !isAdjustmentMode && quantity <= 0) return null;
      if (type === 'entrada' && isAdjustmentMode && quantity === 0) return null;

      if (type === "salida") {
        if (quantity > currentStock) {
          toast({
            title: "Advertencia",
            description: `Stock insuficiente para ${product.code} - Talla: ${size}, Color: ${selectedColor}. Stock disponible: ${currentStock}`,
            variant: "destructive"
          });
          return null;
        }
      }

      // Validación adicional: en ajuste manual con cantidad negativa, no dejar stock final negativo
      if (type === 'entrada' && isAdjustmentMode && quantity < 0) {
        if (Math.abs(quantity) > currentStock) {
          toast({
            title: "Advertencia",
            description: `Stock insuficiente para ajustar ${product.code} - Talla: ${size}, Color: ${selectedColor}. Disponible: ${currentStock}, ajuste: ${quantity}`,
            variant: "destructive"
          });
          return null;
        }
      }

      return {
        product_id: selectedProduct,
        product_code: product.code,
        description: product.description,
        size,
        color: selectedColor,
        quantity,
        price: product.price,
        theoretical_quantity: null,
        counted_quantity: null
      };
    }).filter(Boolean);

    if (newItems.length > 0) {
      setDocument(prev => ({
        ...prev,
        items: [...prev.items, ...newItems]
      }));

      // Mantener el producto seleccionado, limpiar solo el color
      setSelectedColor("");
      // No limpiar selectedProduct ni quantitiesBySize
    }
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    // No limpiar las cantidades al cambiar de color
  };

  const handleQuantityChange = (color, size, value) => {
    // En ajuste manual permitimos valores intermedios como '-' o '' sin convertir
    let nextValue;
    if (isAdjustmentMode) {
      nextValue = value; // mantener string tal cual durante la edición
    } else if (isCountMode) {
      if (value === '') {
        nextValue = '';
      } else {
        const parsed = parseInt(value);
        nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : '';
      }
    } else {
      const parsed = parseInt(value);
      nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }

    setQuantitiesBySize(prev => ({
      ...prev,
      [color]: {
        ...prev[color],
        [size]: nextValue
      }
    }));
  };

  const handleEditQuantity = (productId, color, size, value) => {
    const parsed = parseInt(value);

    if (isCountMode) {
      setDocument(prev => ({
        ...prev,
        items: prev.items.map(item => {
          if (!(item.product_id === productId && item.color === color && item.size === size)) return item;
          const theoreticalQuantity = Number.isFinite(Number(item.theoretical_quantity)) ? Number(item.theoretical_quantity) : 0;
          const countedQuantity = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
          return {
            ...item,
            counted_quantity: countedQuantity,
            quantity: countedQuantity - theoreticalQuantity
          };
        }).filter(item => !(
          item.product_id === productId &&
          item.color === color &&
          item.size === size &&
          item.quantity === 0
        ))
      }));
      return;
    }

    const nextQuantity = isAdjustmentMode ? (Number.isFinite(parsed) ? parsed : 0) : (Number.isFinite(parsed) ? Math.max(0, parsed) : 0);
    setDocument(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId && item.color === color && item.size === size
          ? { ...item, quantity: nextQuantity }
          : item
      )
    }));
  };

  const handleRemoveItem = (index) => {
    setDocument(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const clearQuantities = () => {
    setQuantitiesBySize({});
  };

  const handleEntryModeChange = (value) => {
    if (!isAdmin && value !== 'normal') {
      toast({
        title: 'Acceso denegado',
        description: 'Solo administradores pueden usar ajuste manual o recuento',
        variant: 'destructive'
      });
      return;
    }

    if (document.items.length > 0) {
      toast({
        title: 'Acción no permitida',
        description: 'Vacía primero las líneas del documento para cambiar el modo de entrada',
        variant: 'destructive'
      });
      return;
    }

    clearQuantities();
    setSelectedColor("");
    setDocument(prev => ({
      ...prev,
      entry_mode: value,
      regularization_reason: value === 'normal' ? '' : prev.regularization_reason
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) return;

    if (type === "salida" && !document.customerId) {
      toast({
        title: "Error",
        description: "Debe seleccionar un cliente",
        variant: "destructive"
      });
      return;
    }

    if (document.items.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos un ítem",
        variant: "destructive"
      });
      return;
    }

    if (type === 'entrada' && isSpecialEntryMode && !document.regularization_reason.trim()) {
      toast({
        title: "Error",
        description: "Debe indicar el motivo del ajuste o recuento",
        variant: "destructive"
      });
      return;
    }

    if (type === 'salida' && !isAdmin) {
      toast({
        title: "Acceso denegado",
        description: "Solo los administradores pueden crear documentos de salida",
        variant: "destructive"
      });
      return;
    }

    try {
      // Stock and count comparisons are performed under the server transaction
      // lock, including the previous movement when editing an existing document.
      setSaving(true);
      const result = await saveDocument(document, type);
      if (!result) return;
      if (result.recoveredPreviousOperation) {
        toast({ title: 'Documento anterior confirmado', description: 'El intento anterior ya estaba guardado. No se ha enviado el contenido actual; revise la lista antes de guardar otro documento.' });
        return;
      }

      try {
        // Invalidar caché de inventario para que el siguiente fetch refleje los cambios
        clearInventoryCache();
      } catch (_) {}

      toast({
        title: "Éxito",
        description: result.recoveredPreviousOperation ? 'Se ha confirmado el documento del intento anterior. Revise la lista antes de continuar.' : `Documento de ${type} guardado exitosamente`,
      });

      window.location.hash = `#/${type}s`;
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el documento",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoadingRole) {
    return <div>Cargando...</div>;
  }
  if (loading) {
    return <div>Cargando...</div>;
  }
  if (isLoadingDocument) {
    return <div className="p-6 text-sm">Cargando documento...</div>;
  }

  const selectedProductData = products.find(p => p.id === selectedProduct);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.location.hash = `#/${type}s`}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
            <div>
              <h2 className="text-2xl font-bold mb-2">
                {type === 'entrada' ? 'Entrada de Productos' : 'Salida de Productos'}
              </h2>
              <div className="flex gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Total Unidades:</span> {documentTotals.totalQuantity}
                </div>
                <div>
                  <span className="font-medium">Total Importe:</span> {documentTotals.totalAmount.toFixed(2)} €
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="number">Número</Label>
            <Input
              id="number"
              required
              value={document.number}
              onChange={(e) => setDocument(prev => ({ ...prev, number: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="date">Fecha</Label>
            <Input
              id="date"
              type="date"
              required
              value={document.date || new Date().toISOString().split('T')[0]}
              onChange={(e) => setDocument(prev => ({ ...prev, date: e.target.value || new Date().toISOString().split('T')[0] }))}
            />
          </div>
          {type === "salida" && (
            <div className="col-span-2">
              <Label htmlFor="customerId">Cliente</Label>
              <CustomerSelector
                id="customerId"
                value={document.customerId}
                onSelect={(customerId) => setDocument(prev => ({ ...prev, customerId: customerId }))}
              />
            </div>
          )}
          {type === 'entrada' && (
            <div className="col-span-2 grid grid-cols-2 gap-4 items-end">
              <div>
                <Label htmlFor="entry_mode">Modo de entrada</Label>
                <Select value={entryMode} onValueChange={handleEntryModeChange}>
                  <SelectTrigger id="entry_mode">
                    <SelectValue placeholder="Selecciona el modo de entrada" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTRY_MODE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isSpecialEntryMode && (
                <div className="col-span-2">
                  <Label htmlFor="regularization_reason">Motivo</Label>
                  <textarea
                    id="regularization_reason"
                    value={document.regularization_reason}
                    onChange={(e) => setDocument(prev => ({ ...prev, regularization_reason: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-input px-3 py-2 text-sm"
                    rows={2}
                    placeholder={isCountMode ? "Describe el motivo del recuento" : "Describe el motivo del ajuste"}
                  />
                </div>
              )}
            </div>
          )}
          {type === 'entrada' && isAdjustmentMode && (
            <div className="col-span-2">
              <div className="mt-2 p-3 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 text-sm">
                Modo ajuste manual activo: se permiten cantidades negativas. Los ajustes negativos no pueden dejar el stock por debajo de 0.
              </div>
            </div>
          )}
          {type === 'entrada' && isCountMode && (
            <div className="col-span-2">
              <div className="mt-2 p-3 rounded-md border border-blue-300 bg-blue-50 text-blue-800 text-sm">
                Modo recuento activo: introduces la cantidad contada y la aplicación calcula el ajuste necesario para dejar el stock con ese valor.
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium mb-4">Agregar Productos</h3>
        <div className="space-y-4">
          <div>
            <Label htmlFor="product">Producto</Label>
            <ProductSelector
              value={selectedProduct}
              onSelect={(productId) => {
                setSelectedProduct(productId);
                setSelectedColor("");
              }}
            />
          </div>

          {selectedProductData && (
            <>
              <div>
                <Label htmlFor="colors">Color</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedProductData.colors.map((color) => (
                    <Button
                      key={color}
                      type="button"
                      variant={selectedColor === color ? "default" : "outline"}
                      onClick={() => handleColorSelect(color)}
                      className="flex items-center gap-2"
                    >
                      <ColorBadge color={color} />
                    </Button>
                  ))}
                </div>
              </div>

              {selectedColor && (
                <div className="mt-4">
                  <Label>{isCountMode ? 'Cantidades contadas por talla' : 'Cantidades por talla'}</Label>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ColorBadge color={selectedColor} />
                        <span className="font-medium">{selectedColor}</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearQuantities}
                        className="flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Limpiar cantidades
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedProductData.sizes
                        .sort((a, b) => {
                          const sizeOrder = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
                          return sizeOrder.indexOf(a) - sizeOrder.indexOf(b);
                        })
                        .map(size => (
                        <div key={size} className="flex flex-col">
                          <Label htmlFor={`quantity-${selectedColor}-${size}`} className="text-xs mb-1">{size}</Label>
                          {isCountMode && (
                            <span className="text-[11px] text-gray-500 mb-1">
                              Stock: {calculateCurrentStock(selectedProduct, size, selectedColor)}
                            </span>
                          )}
                          <Input
                            id={`quantity-${selectedColor}-${size}`}
                            type="number"
                            min={isAdjustmentMode ? -999 : 0}
                            max="999"
                            value={(quantitiesBySize[selectedColor]?.[size] ?? (isCountMode ? '' : 0))}
                            onChange={(e) => handleQuantityChange(selectedColor, size, e.target.value)}
                            className="w-16 h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          {isCountMode && (
                            <span className="text-[11px] text-gray-500 mt-1">
                              Ajuste: {(() => {
                                const raw = quantitiesBySize[selectedColor]?.[size];
                                if (raw === '' || raw === undefined || raw === null) return '—';
                                const counted = parseInt(raw);
                                if (!Number.isFinite(counted)) return '—';
                                return counted - calculateCurrentStock(selectedProduct, size, selectedColor);
                              })()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <Button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!selectedProduct || !selectedColor}
                >
                  <Plus className="h-4 w-4 mr-2" /> Agregar productos
                </Button>
              </div>

              {(type === "salida" || (type === 'entrada' && isSpecialEntryMode)) && selectedColor && (
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-sm font-medium mb-2">Stock actual por combinación:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {selectedProductData.sizes.map(size => {
                      const stock = calculateCurrentStock(selectedProduct, size, selectedColor);
                      return (
                        <div key={`${size}-${selectedColor}`} className="text-sm">
                          {size}: <span className={stock === 0 ? 'text-red-500 font-bold' : 'font-medium'}>{stock}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Producto</TableHead>
                <TableHead className="w-[80px]">Imagen</TableHead>
                <TableHead className="w-[80px]">Color</TableHead>
                  {allSizes.map(size => (
                    <TableHead key={size} className="text-center w-[70px]">
                      {isCountMode ? `${size} (Cont.)` : size}
                    </TableHead>
                  ))}
                <TableHead className="text-center w-[80px]">{isCountMode ? 'Total Aj.' : 'Total Unid.'}</TableHead>
                <TableHead className="text-center w-[80px]">Precio</TableHead>
                <TableHead className="text-center w-[100px]">Total</TableHead>
                <TableHead className="text-right w-[60px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(
                document.items.reduce((acc, item) => {
                  const key = `${item.product_id}-${item.color}`;
                  if (!acc[key]) {
                    const product = products.find(p => p.id === item.product_id);
                    acc[key] = {
                      product_id: item.product_id,
                      product_code: item.product_code,
                      description: item.description,
                      color: item.color,
                      sizes: {},
                      counted_sizes: {},
                      adjustments: {},
                      total: 0,
                      total_quantity: 0,
                      price: item.price,
                      product: product,
                      available_sizes: product?.sizes || []
                    };
                  }
                  acc[key].sizes[item.size] = isCountMode
                    ? (Number.isFinite(Number(item.counted_quantity)) ? Number(item.counted_quantity) : 0)
                    : item.quantity;
                  acc[key].counted_sizes[item.size] = Number.isFinite(Number(item.counted_quantity)) ? Number(item.counted_quantity) : null;
                  acc[key].adjustments[item.size] = item.quantity;
                  acc[key].total_quantity += item.quantity;
                  acc[key].total += item.quantity * item.price;
                  return acc;
                }, {})
              ).map(([key, item]) => (
                <TableRow key={key}>
                  <TableCell className="max-w-[250px] truncate">
                    {item.product_code} - {item.description}
                  </TableCell>
                  <TableCell>
                    {item.product?.images?.length > 0 && (
                      <div className="w-16 h-16 flex-shrink-0">
                        <img
                          src={item.product.images[item.product.main_image_index || 0]}
                          alt={item.description}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <ColorBadge color={item.color} />
                  </TableCell>
                  {allSizes.map(size => (
                    <TableCell key={size} className="text-center p-1">
                      {item.available_sizes.includes(size) ? (
                        <div className="flex flex-col items-center gap-1">
                          <Input
                            type="number"
                            min={isAdjustmentMode ? -999 : 0}
                            max="999"
                            value={(() => {
                              const rowItem = document.items.find(i => 
                                i.product_id === item.product_id && 
                                i.color === item.color && 
                                i.size === size
                              );
                              if (!rowItem) return isCountMode ? '' : 0;
                              return isCountMode
                                ? (Number.isFinite(Number(rowItem.counted_quantity)) ? rowItem.counted_quantity : '')
                                : rowItem.quantity;
                            })()}
                            onChange={(e) => handleEditQuantity(item.product_id, item.color, size, e.target.value)}
                            className="w-16 h-8 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          {isCountMode && (
                            <span className="text-[11px] text-gray-500 text-center">
                              Teo: {document.items.find(i =>
                                i.product_id === item.product_id &&
                                i.color === item.color &&
                                i.size === size
                              )?.theoretical_quantity ?? 0} · Aj: {item.adjustments[size] ?? 0}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="text-sm font-medium">{item.total_quantity}</span>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="text-sm text-gray-600">{item.price.toFixed(2)} €</span>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    <span className="font-medium">{item.total.toFixed(2)} €</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        setDocument(prev => ({
                          ...prev,
                          items: prev.items.filter(i => 
                            !(i.product_id === item.product_id && i.color === item.color)
                          )
                        }));
                      }}
                      disabled={saving}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.location.hash = `#/${type}s`}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar Documento'
          )}
        </Button>
      </div>
    </form>
  );
}
